import { sessionQueryVersionAtom } from '@/atoms/sessionAtoms';
import { compareSessionsByStatus } from '@/components/ui/session-status/session-status-order';
import { isSessionUrgent } from '@/components/ui/session-urgency/types';
import {
  IS_UPCOMING_SESSIONS_ENABLED,
  useIsSessionUrgencyEnabled,
} from '@/features/flags';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import {
  isUrgentOnlyFilterAtom,
  selectedDateRangeFilterAtom,
  selectedPatientProfilesFilterAtom,
  selectedStatusesFilterAtom,
  sessionsSortByAtom,
  sessionsSortOrderAtom,
} from '@heidi/session/data';
import { isBefore } from 'date-fns';
import { useAtomValue } from 'jotai';
import { orderBy } from 'lodash-es';
import { useMemo } from 'react';
import { useSessionSearch } from './useSessionSearch';

/** Filter out upcoming sessions (sessions with future dates). */
function filterUpcomingSessions<
  T extends { created_time_local?: string | null; ehr_appt_id?: string | null },
>(sessions: T[] | undefined, hasDateRangeFilter: boolean): T[] | undefined {
  if (!IS_UPCOMING_SESSIONS_ENABLED() || hasDateRangeFilter) {
    return sessions;
  }
  return sessions?.filter((session) => {
    const sessionDate = parseTimeStamp(session.created_time_local, !session.ehr_appt_id);
    return isBefore(sessionDate, new Date());
  });
}

export const useSessionsFilteredList = () => {
  const { searchValue, sessionSearchResult, totalMatchCount } = useSessionSearch();

  const isSessionUrgencyEnabled = useIsSessionUrgencyEnabled();
  const isUrgentOnlyFilter = useAtomValue(isUrgentOnlyFilterAtom);
  const selectedStatusesFilter = useAtomValue(selectedStatusesFilterAtom);
  const selectedDateRangeFilter = useAtomValue(selectedDateRangeFilterAtom);
  const selectedPatientProfilesFilter = useAtomValue(selectedPatientProfilesFilterAtom);
  const sessionsSortBy = useAtomValue(sessionsSortByAtom);
  const sessionsSortOrder = useAtomValue(sessionsSortOrderAtom);
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);

  const hasDateRangeFilter = Boolean(
    selectedDateRangeFilter.from || selectedDateRangeFilter.to
  );

  const filteredSessionSearchResult = useMemo(() => {
    // v3 delegates filtering/sorting to the backend via useQuerySessionsWithPagination,
    // which sends status, date range, patient profile, sort, and urgency params server-side.
    if (sessionQueryVersion === 'v3') {
      return filterUpcomingSessions(sessionSearchResult, hasDateRangeFilter);
    }

    let filteredSessions = sessionSearchResult;

    // v1/v2's plain `GET /sessions/` endpoint has no `is_urgent` param (only
    // v2-2 and /search do — see docs/contracts/session-urgency.md), so urgency
    // filters client-side here like the sibling filters below. Complete, not
    // page-partial: opening the filter dropdown flips the list to v1, which
    // loads ALL sessions before this filter can be toggled.
    if (isSessionUrgencyEnabled && isUrgentOnlyFilter) {
      filteredSessions = filteredSessions?.filter(isSessionUrgent);
    }

    if (selectedStatusesFilter.length > 0) {
      filteredSessions = filteredSessions?.filter(
        (session) =>
          session.session_status &&
          selectedStatusesFilter.includes(session.session_status)
      );
    }

    if (hasDateRangeFilter) {
      const fromDate = selectedDateRangeFilter.from;
      const toDate = selectedDateRangeFilter.to;

      filteredSessions = filteredSessions?.filter((session) => {
        const sessionDate = parseTimeStamp(
          session.created_time_local || session.created_at,
          !session.ehr_appt_id
        );
        return (
          (!fromDate || sessionDate >= fromDate) && (!toDate || sessionDate <= toDate)
        );
      });
    } else {
      filteredSessions = filterUpcomingSessions(filteredSessions, hasDateRangeFilter);
    }

    if (selectedPatientProfilesFilter.length > 0) {
      filteredSessions = filteredSessions?.filter((session) => {
        return selectedPatientProfilesFilter.includes(session.patient_profile?.id ?? '');
      });
    }

    if (filteredSessions) {
      if (sessionsSortBy === 'status') {
        return [...filteredSessions].sort((a, b) =>
          compareSessionsByStatus(a, b, sessionsSortOrder)
        );
      } else if (sessionsSortBy === 'createdAt') {
        return filteredSessions?.slice().sort((a, b) => {
          const sessionTimeA = parseTimeStamp(
            a.created_time_local ?? a.created_at,
            !a.ehr_appt_id
          );
          const sessionTimeB = parseTimeStamp(
            b.created_time_local ?? b.created_at,
            !b.ehr_appt_id
          );
          return sessionsSortOrder === 'asc'
            ? sessionTimeA.getTime() - sessionTimeB.getTime()
            : sessionTimeB.getTime() - sessionTimeA.getTime();
        });
      } else if (sessionsSortBy === 'urgency') {
        // Flag off: leave the default order (no urgency reorder).
        if (!isSessionUrgencyEnabled) return filteredSessions;
        // Float urgent sessions over the loaded rows (client-side for now).
        const rank = (session: (typeof filteredSessions)[number]) =>
          isSessionUrgent(session) ? 1 : 0;
        return [...filteredSessions].sort((a, b) => {
          const diff = rank(b) - rank(a);
          return sessionsSortOrder === 'asc' ? -diff : diff;
        });
      }
      const sortKeyMap = {
        updatedAt: 'updated_at',
        name: 'session_name',
      } satisfies Record<typeof sessionsSortBy, string>;
      const sortKey = sortKeyMap[sessionsSortBy];
      return orderBy(filteredSessions, [sortKey], [sessionsSortOrder]);
    }

    return filteredSessions;
  }, [
    sessionQueryVersion,
    sessionSearchResult,
    isSessionUrgencyEnabled,
    isUrgentOnlyFilter,
    selectedStatusesFilter,
    selectedDateRangeFilter.from,
    selectedDateRangeFilter.to,
    hasDateRangeFilter,
    selectedPatientProfilesFilter,
    sessionsSortBy,
    sessionsSortOrder,
  ]);

  return {
    sessionSearchResult: filteredSessionSearchResult,
    searchValue,
    totalMatchCount,
  };
};
